<template>
    <div class="vrm-ai-assistant">
        <!-- Sidebar -->
        <div
            class="vrm-ai-assistant__sidebar"
            :class="{ 'vrm-ai-assistant__sidebar--open': isSidebarOpen }"
        >
            <div class="vrm-ai-assistant__sidebar-header">
                <router-link id="vrm-ai-assistant__sidebar-logo" to="base.installation-overview">
                    <img src="img/victron_logo_white.svg" alt="Victron Logo" />
                </router-link>
                <button
                    id="vrm-ai-assistant__sidebar-close-button"
                    class="vrm-ai-assistant__sidebar-close-button"
                    aria-label="Close sidebar"
                    @click="isSidebarOpen = false"
                >
                    <span class="sg-vrm-icon_055_cross-16px"></span>
                </button>
            </div>
            <div class="vrm-ai-assistant__sidebar-button-container">
                <button
                    id="vrm-ai-assistant__start-new-chat-button"
                    class="vrm-ai-assistant__sidebar-button sg-btn sg-blue sg-element"
                    aria-label="Start a new chat"
                    @click="createNewChat"
                >
                    {{ $t('ai-assistant.start_new_chat', { i18nFallback: 'Begin a new chat' }) }}
                </button>
            </div>
            <!-- Sidebar previous chats -->
            <div class="vrm-ai-assistant__sidebar-content">
                <div class="vrm-ai-assistant__sidebar-previous-chats">
                    <div
                        v-for="(chat, index) in chats"
                        :key="chat.chatIdentifier"
                        class="vrm-ai-assistant__sidebar-previous-chats-item-container"
                        :class="{
                            'vrm-ai-assistant__sidebar-previous-chats-item-container--active':
                                chat.chatIdentifier === currentChatIdentifier,
                            'vrm-ai-assistant__sidebar-previous-chats-item-container--popup-active':
                                activeChatOptionsPopup === chat.chatIdentifier,
                        }"
                    >
                        <button
                            :id="
                                `vrm-ai-assistant__sidebar-previous-chats-item--${index}--${chat.createdAt}--${chat.chatIdentifier}`
                            "
                            class="vrm-ai-assistant__sidebar-previous-chats-item"
                            :class="{
                                'vrm-ai-assistant__sidebar-previous-chats-item--renaming':
                                    renamingChatIdentifier === chat.chatIdentifier,
                            }"
                            :aria-label="`Open chat ${chat.name}`"
                            @click="openChat(chat)"
                        >
                            <span
                                v-if="renamingChatIdentifier !== chat.chatIdentifier"
                                class="vrm-ai-assistant__sidebar-previous-chats-item-name"
                            >
                                {{ chat.name }}
                            </span>
                            <input
                                v-else
                                :ref="`renameInput-${chat.chatIdentifier}`"
                                v-model="renameInputValue"
                                class="vrm-ai-assistant__sidebar-previous-chats-item-name-input"
                                type="text"
                                @blur="saveRename(chat)"
                                @keydown.enter.prevent="saveRename(chat)"
                                @keydown.escape="cancelRename"
                                @click.stop
                            />
                            <div class="vrm-ai-assistant__sidebar-previous-chats-item-more">
                                <button
                                    :id="
                                        `vrm-ai-assistant__sidebar-more-button--${chat.chatIdentifier}`
                                    "
                                    class="vrm-ai-assistant__sidebar-more-button"
                                    aria-label="More options"
                                    @click.stop="
                                        toggleChatOptionsPopup(chat.chatIdentifier, $event)
                                    "
                                >
                                    <span class="sg-vrm-icon_154_more-24px"></span>
                                </button>

                                <div
                                    v-if="activeChatOptionsPopup === chat.chatIdentifier"
                                    :id="
                                        `vrm-ai-assistant__chat-options-popup--${chat.chatIdentifier}`
                                    "
                                    class="vrm-ai-assistant__chat-options-popup vrm-ai-assistant__chat-options-popup--vertical"
                                    @click.stop
                                >
                                    <button
                                        class="vrm-ai-assistant__chat-options-popup-item"
                                        @click="renameChatAction(chat)"
                                    >
                                        <span class="sg-vrm-icon_075_edit-16px"></span>
                                        <span>
                                            {{
                                                $t('ai-assistant.rename_chat', {
                                                    i18nFallback: 'Rename',
                                                })
                                            }}
                                        </span>
                                    </button>
                                    <button
                                        class="vrm-ai-assistant__chat-options-popup-item vrm-ai-assistant__chat-options-popup-item--delete"
                                        @click="deleteChatAction(chat)"
                                    >
                                        <span class="sg-vrm-icon_121_trash-16px"></span>
                                        <span>
                                            {{
                                                $t('ai-assistant.delete_chat', {
                                                    i18nFallback: 'Delete',
                                                })
                                            }}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="vrm-ai-assistant__content">
            <!-- Header section -->
            <div class="vrm-ai-assistant__content-header">
                <button
                    id="vrm-ai-assistant__sidebar-toggle-button"
                    class="vrm-ai-assistant__content-header-toggle"
                    aria-label="Open sidebar"
                    @click="onNavToggle"
                >
                    <span class="sg-vrm-icon_032_menu-16px" />
                </button>
                <h2>{{ $t('ai-assistant.ai_assistant', { i18nFallback: 'VRM AI Assistant' }) }}</h2>
                <button
                    id="vrm-ai-assistant__close-page-button"
                    class="vrm-ai-assistant__close-button"
                    aria-label="Close AI assistant"
                    @click="closeAIAssistant"
                >
                    <span class="sg-vrm-icon_055_cross-16px"></span>
                </button>
            </div>
            <!-- Chat section -->
            <template v-if="currentChatIdentifier && currentChat">
                <div ref="chatContainer" class="vrm-ai-assistant__content-body">
                    <!-- Chat container -->
                    <div class="vrm-ai-assistant__content-body-chat-container">
                        <!-- Timestamp of first message -->
                        <div class="vrm-ai-assistant__content-body-chat-timestamp">
                            {{ currentChatTimestamp }}
                        </div>
                        <!-- Chat content -->
                        <div class="vrm-ai-assistant__content-body-chat-content">
                            <!-- Chat group of messages from AI or user -->
                            <div
                                v-for="(group, index) in groupedMessages"
                                :key="group.messages[0].idAiChatMessage"
                                class="vrm-ai-assistant__content-body-chat-item"
                                :class="{
                                    'vrm-ai-assistant__content-body-chat-item--assistant':
                                        group.isGeneratedMessage,
                                    'vrm-ai-assistant__content-body-chat-item--user': !group.isGeneratedMessage,
                                }"
                            >
                                <!-- Chat avatar -->
                                <div class="vrm-ai-assistant__content-body-chat-avatar">
                                    <span
                                        v-if="group.isGeneratedMessage"
                                        class="sg-vrm-icon_149_ai-chat"
                                    ></span>
                                    <vrm-avatar v-else :user="currentUser" />
                                </div>
                                <div class="vrm-ai-assistant__content-body-chat-messages-container">
                                    <!-- Chat message item -->
                                    <div
                                        v-for="message in group.messages"
                                        :key="message.idAiChatMessage"
                                    >
                                        <!-- Chat message content-->
                                        <div
                                            class="vrm-ai-assistant__content-body-chat-message"
                                            :class="{
                                                'vrm-ai-assistant__content-body-chat-message--failed':
                                                    message.sendMessageFailed,
                                            }"
                                            v-html="message.message"
                                        ></div>
                                        <!-- Retry button -->
                                        <div
                                            v-if="message.sendMessageFailed"
                                            class="vrm-ai-assistant__content-body-chat-message-actions"
                                        >
                                            <button
                                                id="vrm-ai-assistant__resend-message-button"
                                                class="sg-vrm-icon_153_refresh"
                                                aria-label="Retry sending message"
                                                @click="sendMessage(true, message)"
                                            ></button>
                                        </div>
                                        <!-- Like/Dislike buttons -->
                                        <div
                                            v-if="group.isGeneratedMessage && index > 0"
                                            class="vrm-ai-assistant__content-body-chat-message-actions"
                                        >
                                            <button
                                                id="vrm-ai-assistant__like-message-button"
                                                class="sg-vrm-icon_152_like"
                                                :class="{
                                                    'vrm-ai-assistant__content-body-chat-message-action--active':
                                                        message.messageRating === 5,
                                                }"
                                                aria-label="Like message"
                                                @click="rateMessage(message, 5)"
                                            ></button>
                                            <button
                                                id="vrm-ai-assistant__dislike-message-button"
                                                class="sg-vrm-icon_151_dislike"
                                                :class="{
                                                    'vrm-ai-assistant__content-body-chat-message-action--active':
                                                        message.messageRating === 1,
                                                }"
                                                aria-label="Dislike message"
                                                @click="rateMessage(message, 1)"
                                            ></button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Loading message -->
                            <div
                                v-if="loading"
                                class="vrm-ai-assistant__content-body-chat-item vrm-ai-assistant__content-body-chat-item--assistant"
                            >
                                <div class="vrm-ai-assistant__content-body-chat-avatar">
                                    <span class="sg-vrm-icon_149_ai-chat"></span>
                                </div>
                                <div class="vrm-ai-assistant__content-body-chat-messages">
                                    <div
                                        class="vrm-ai-assistant__content-body-chat-message vrm-ai-assistant__content-body-chat-message--loading"
                                    >
                                        <span
                                            class="vrm-ai-assistant__content-body-chat-message--loading-dot"
                                        ></span>
                                        <span
                                            class="vrm-ai-assistant__content-body-chat-message--loading-dot"
                                        ></span>
                                        <span
                                            class="vrm-ai-assistant__content-body-chat-message--loading-dot"
                                        ></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Chat result preview -->
                    <div class="vrm-ai-assistant__content-body-chat-result-preview">
                        <div class="vrm-ai-assistant__content-body-chat-result-preview-content">
                            <h3>
                                {{
                                    $t('ai-assistant.chart_preview', {
                                        i18nFallback: 'Chart preview',
                                    })
                                }}
                            </h3>
                            <template v-if="isWidgetPreviewAvailable">
                                <graph-preview
                                    :widget="widget"
                                    :installation="installation"
                                    :start-date="last24HoursTimestamp"
                                    :end-date="currentTimestamp"
                                    :is-advanced-view="true"
                                    :is-ai-assistant="true"
                                />
                                <template v-if="hasWritePermissions">
                                    <button
                                        v-if="!widgetSaved"
                                        id="vrm-ai-assistant__save-widget-button"
                                        class="vrm-ai-assistant__save-widget-button sg-btn sg-blue"
                                        :class="{ 'sg-disabled': loading }"
                                        :aria-label="
                                            $t('ai-assistant.save_widget', {
                                                i18nFallback: 'Add to advanced page',
                                            })
                                        "
                                        @click="saveWidget"
                                    >
                                        {{
                                            $t('ai-assistant.save_widget', {
                                                i18nFallback: 'Add to advanced page',
                                            })
                                        }}
                                    </button>
                                    <button
                                        v-else
                                        id="vrm-ai-assistant__go-to-advanced-page-button"
                                        class="vrm-ai-assistant__go-to-advanced-page-button sg-btn sg-site"
                                        :aria-label="
                                            $t('ai-assistant.go_to_advanced_page', {
                                                i18nFallback: 'Go to advanced page',
                                            })
                                        "
                                        @click="goToAdvancedPage"
                                    >
                                        {{
                                            $t('ai-assistant.go_to_advanced_page', {
                                                i18nFallback: 'Go to advanced page',
                                            })
                                        }}
                                        <span class="sg-vrm-icon_049_arrow-right-16px"></span>
                                    </button>
                                </template>
                            </template>
                            <span v-else>
                                {{
                                    $t('ai-assistant.chart_preview_description', {
                                        i18nFallback:
                                            'The chart will show up here as soon as you finish your prompt',
                                    })
                                }}
                            </span>
                        </div>
                    </div>
                </div>
                <!-- Chat send message form -->
                <div class="vrm-ai-assistant__content-footer">
                    <form class="vrm-ai-assistant__chat-form" @submit.prevent="sendMessage(false)">
                        <textarea
                            ref="messageTextarea"
                            v-model="inputMessage"
                            :disabled="loading"
                            class="sg-element"
                            placeholder="Type here"
                            :maxlength="maxInputCharacters"
                            @keydown.enter.exact.prevent="sendMessage(false)"
                        />
                        <button
                            id="vrm-ai-assistant__send-message-button"
                            class="vrm-circle-icon-button sg-btn"
                            type="submit"
                            :class="{ 'sg-disabled': loading || !canSendMessage }"
                        >
                            <span class="sg-vrm-icon_150_send"></span>
                        </button>
                    </form>
                    <div class="vrm-ai-assistant__content-footer-message">
                        <span>
                            {{
                                $t('ai-assistant.powered_by_ai_note', {
                                    i18nFallback: 'This is powered by AI and may make mistakes.',
                                })
                            }}
                        </span>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import VrmHeader from '@/vue/components/layout/VrmHeader';
import VrmTitle from '@/vue/components/layout/VrmTitle';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import VrmAvatar from '@/vue/components/shared/VrmAvatar';
import GraphPreview from '@/vue/components/shared/custom-widgets/GraphPreview';
import {
    DEFAULT_Y_AXIS_SCALES,
    Y_AXIS_CUSTOMISATION_OPTIONS,
} from '@/enums/custom-widget-axis-customisation';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import {
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATION_TYPE_ERROR,
} from '@/vue/store/modules/notifications';
import { marked } from 'marked';
import moment from 'moment';
import { parseDevices, parseParameters } from '@/vue/components/shared/custom-widgets/helper';
import ConfirmDeletionModal from '@/vue/components/modals/ConfirmDeletionModal.vue';

@Component({
    components: { VrmHeader, VrmTitle, VrmAvatar, GraphPreview },
})
@AngularRoute('ai-assistant', '/installation/:id/ai-assistant', {
    slot: 'base',
})
@AngularLocaleBundle('custom_widgets')
@AngularLocaleBundle('errors')
@AngularLocaleBundle('ai-assistant')
export default class AIAssistantView extends Vue {
    @ngInject()
    InstallationsService;
    @ngInject()
    UserService;
    @ngInject()
    ApiService;
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    vrmUserAccessLevel;
    @ngInject()
    VrmModalService;

    installation = null;
    attributes = [];
    devices = {};

    isSidebarOpen = false;
    loading = false;
    chats = [];
    maxInputCharacters = 1000;
    currentChatIdentifier = null;
    inputMessage = '';
    widgetSaved = false;
    lastMessageWithWidget = null;
    activeChatOptionsPopup = null;
    renamingChatIdentifier = null;
    renameInputValue = '';
    savedWidgetId = null;
    widget = {
        title: '',
        parameters: [],
        yAxisProperties: {
            method: this.localizedYAxisCustomisationOptions[DEFAULT_Y_AXIS_SCALES - 1],
            min: null,
            max: null,
            interval: null,
        },
    };

    get idSite() {
        return this.$route.params.id;
    }

    get currentChat() {
        return this.chats.find(chat => chat.chatIdentifier === this.currentChatIdentifier);
    }

    get currentChatTimestamp() {
        return moment.unix(this.currentChat.createdAt).format('ddd LT');
    }

    get currentUser() {
        return this.UserService.user;
    }

    get last24HoursTimestamp() {
        return moment()
            .subtract(24, 'hours')
            .unix();
    }

    get currentTimestamp() {
        return moment().unix();
    }

    get canSendMessage() {
        return this.inputMessage.trim().length > 0;
    }

    get hasWritePermissions() {
        return (
            this.installation &&
            this.installation.view_permissions &&
            this.installation.view_permissions.update_settings
        );
    }

    get isDemo() {
        return (
            this.UserService.user &&
            this.UserService.user.accessLevel === this.vrmUserAccessLevel.DEMO
        );
    }

    @Watch('idSite', { immediate: true })
    async loadSiteData() {
        if (!this.idSite) return;
        try {
            this.installation = await this.InstallationsService.getInstallation(this.idSite);
            await this.UserService.getUser();
            if (!this.hasWritePermissions && !this.isDemo) {
                throw new Error('User does not have sufficient permissions');
            }
        } catch (error) {
            this.$router.push({
                name: 'base.installation.dashboard',
                params: { id: this.$route.params.id },
            });
            return;
        }

        await this.getAttributes();
        await this.fetchChats();

        // If no previous chats or the last chat includes a message from the user, create a new one and open it
        if (
            this.chats.length === 0 ||
            this.chats[0].messages.some(message => !message.isGeneratedMessage)
        ) {
            await this.createNewChat();
        }

        // If user has previous chats and there is no chat open, open the last one
        if (this.chats.length > 0 && !this.currentChatIdentifier) {
            this.openChat(this.chats[0]);
            return;
        }
    }

    async fetchChats() {
        return this.ApiService.call2(`/installations/${this.idSite}/ai-chats/list`, null, {
            method: 'GET',
            skipResultChecks: true,
        })
            .then(response => {
                this.chats = response.data.aiChats.sort((a, b) => b.createdAt - a.createdAt);
                this.maxInputCharacters = response.data.messageMaxChars;
            })
            .catch(() => {
                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_ERROR,
                    message: this.$t('errors.error_loading_data', {
                        i18nFallback: 'There was an error loading the data, please try again.',
                    }),
                });
                // Throw an error so that the execution of the function stops since having the chats is essential for this component to work
                throw new Error('Error loading chats');
            });
    }

    async getAttributes() {
        return this.ApiService.getAttributes(this.idSite).then(result => {
            this.attributes = result.attributes.map(attribute => ({
                idDataAttribute: +attribute.idDataAttribute,
                code: attribute.code,
            }));
            this.devices = parseDevices(result.devices, result.attributes, this.installation);
        });
    }

    onNavToggle() {
        this.isSidebarOpen = !this.isSidebarOpen;
    }

    closeAIAssistant() {
        const goTo =
            localStorage.getItem('vrm-ai-assistant-redirected-from') ||
            'base.installation.dashboard';
        this.$router.push({
            name: goTo,
            params: { id: this.$route.params.id },
        });
    }

    openChat(chat) {
        if (this.currentChatIdentifier === chat.chatIdentifier) return;
        this.currentChatIdentifier = chat.chatIdentifier;
        this.isSidebarOpen = false;
        this.loading = false;
        this.lastMessageWithWidget = null;
        this.widgetSaved = false;
        this.activeChatOptionsPopup = null;
        this.scrollToBottom();
        this.focusMessageTextarea();
        this.widget = {
            ...this.widget,
            title: '',
            parameters: [],
        };
        this.updateWidget();
    }

    /**
     * Messages grouped by all the messages from the same user or AI
     * This is useful so we can show the user/AI avatar only once per group
     */
    get groupedMessages() {
        const sortedMessages = this.currentChat.messages
            .sort((a, b) => a.idAiChatMessage - b.idAiChatMessage)
            .map(message => {
                // parse markdown in the message if it is sent by the AI
                if (message.isGeneratedMessage) {
                    message.message = marked.parse(message.message);
                } else {
                    message.message = `<p>${message.message}</p>`;
                }
                return message;
            });
        const groupedMessages = [
            {
                messages: [sortedMessages[0]],
                isGeneratedMessage: sortedMessages[0].isGeneratedMessage,
            },
        ];
        // Add messages from the same user or AI to the same group until the sender of the next message changes
        for (let i = 0; i < sortedMessages.length - 1; i++) {
            const prevMessage = sortedMessages[i];
            const currentMessage = sortedMessages[i + 1];
            const lastMessageGroup = groupedMessages[groupedMessages.length - 1];
            if (prevMessage.isGeneratedMessage === currentMessage.isGeneratedMessage) {
                lastMessageGroup.messages.push(currentMessage);
            } else {
                groupedMessages.push({
                    messages: [currentMessage],
                    isGeneratedMessage: currentMessage.isGeneratedMessage,
                });
            }
        }
        return groupedMessages;
    }

    async createNewChat() {
        return this.ApiService.call2(`/installations/${this.idSite}/ai-chats/start`, null, {
            method: 'POST',
            skipResultChecks: true,
            throwOnError: true,
        })
            .then(response => {
                const newChat = response.data.aiChat;
                this.chats.unshift(newChat);
                this.openChat(newChat);
                return newChat;
            })
            .catch(error => {
                if (this.checkForAIServiceUnavailableError(error)) return;
                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_ERROR,
                    message: this.$t('ai-assistant.error_creating_chat', {
                        i18nFallback: 'There was an error creating a new chat, please try again.',
                    }),
                });
            });
    }

    scrollToBottom() {
        const isMobile = window.innerWidth < 1280;
        this.$nextTick(() => {
            const scrollIndex =
                this.$refs.chatContainer.scrollHeight - this.$refs.chatContainer.clientHeight;
            // If widget preview is available, don't scroll all the way to the bottom and leave some space for the last message to be visible
            const mobileScrollIndex = this.isWidgetPreviewAvailable
                ? scrollIndex - 200
                : scrollIndex;
            this.$refs.chatContainer.scrollTop = isMobile ? mobileScrollIndex : scrollIndex;
        });
    }

    async sendMessage(retry = false, messageObject = {}) {
        const message = retry ? messageObject.message : this.inputMessage.trim();
        if (!this.canSendMessage) return;
        this.loading = true;
        if (!retry) {
            this.inputMessage = '';
            const lastMessage = this.currentChat.messages[this.currentChat.messages.length - 1];
            // Add the message to the chat and set the failed status to true later if the request fails
            // This is useful so we can show the user the message was sent and failed but the message appears in the chat for the user to retry
            this.currentChat.messages.push({
                idAiChatMessage: lastMessage.idAiChatMessage + 1,
                message,
                isGeneratedMessage: false,
            });
        }
        this.scrollToBottom();
        window.dataLayer.push({
            event: 'gtag.ai_assistant_send_message',
            chatIdentifier: this.currentChatIdentifier,
        });
        await this.ApiService.call2(
            `/installations/${this.idSite}/ai-chat-messages/send`,
            { chatIdentifier: this.currentChatIdentifier, message },
            { method: 'POST', skipResultChecks: true, throwOnError: true },
        )
            .then(async response => {
                if (!retry) {
                    this.currentChat.messages.push(response.data.aiChatResponse);
                    this.currentChat.name = response.data.aiChatName;
                    this.updateWidget();
                } else {
                    await this.fetchChats();
                }
                this.scrollToBottom();
            })
            .catch(error => {
                if (this.checkForAIServiceUnavailableError(error)) return;
                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_ERROR,
                    message: this.$t('ai-assistant.error_sending_message', {
                        i18nFallback: 'There was an error sending your message, please try again.',
                    }),
                });
                this.currentChat.messages[
                    this.currentChat.messages.length - 1
                ].sendMessageFailed = true;
                return;
            })
            .finally(() => {
                this.loading = false;
                this.focusMessageTextarea();
            });
    }

    focusMessageTextarea() {
        this.$nextTick(() => {
            this.$refs.messageTextarea.focus();
        });
    }

    saveWidget() {
        this.loading = true;
        this.ApiService.call2(
            `/installations/${this.idSite}/ai-chat-messages/${this.lastMessageWithWidget.idAiChatMessage}/save-widget`,
            null,
            { method: 'POST', skipResultChecks: true },
        )
            .then(response => {
                this.savedWidgetId = response.data.idCustomWidget;

                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_SUCCESS,
                    message: this.$t('ai-assistant.widget_saved', {
                        i18nFallback: 'Widget saved successfully!',
                    }),
                });
                this.widgetSaved = true;
            })
            .catch(() => {
                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_ERROR,
                    message: this.$t('ai-assistant.error_saving_widget', {
                        i18nFallback: 'There was an error saving the widget, please try again.',
                    }),
                });
                this.widgetSaved = false;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    goToAdvancedPage() {
        const navigationOptions = {
            name: 'base.installation.advanced',
            params: { id: this.$route.params.id },
        };

        // Add widget ID to query parameters if available for scrolling
        if (this.savedWidgetId) {
            navigationOptions.query = { widgetId: this.savedWidgetId };
        }

        this.$router.push(navigationOptions);
    }

    rateMessage(message, newRating) {
        const originalRating = message.messageRating;
        // Set rating to null if it is the same as the original rating to undo the rating
        const rating = newRating === originalRating ? null : newRating;
        message.messageRating = rating;
        this.ApiService.call2(
            `/installations/${this.idSite}/ai-chat-messages/${message.idAiChatMessage}/rating`,
            { messageRating: rating },
            { method: 'PATCH', skipResultChecks: true },
        ).catch(() => {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('ai-assistant.error_rating_message', {
                    i18nFallback: 'There was an error rating the message, please try again.',
                }),
            });
            message.messageRating = originalRating;
            return;
        });
    }

    get isWidgetPreviewAvailable() {
        return this.lastMessageWithWidget;
    }

    checkForAIServiceUnavailableError(error) {
        if (error.data.error_code === 'ai_service_unavailable') {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('ai-assistant.ai_service_unavailable', {
                    i18nFallback:
                        'The AI service is currently unavailable, please try again later.',
                }),
            });
            return true;
        }
        return false;
    }

    async updateWidget() {
        if (!this.currentChat || !this.currentChat.messages) return null;
        this.lastMessageWithWidget = this.currentChat.messages
            .reverse()
            .find(message => message.widgetJson);
        if (!this.lastMessageWithWidget || !this.lastMessageWithWidget.widgetJson) return null;
        await this.$nextTick();
        // Map dataAttribute to idDataAttribute which is required by parseParameters
        const parameters = this.lastMessageWithWidget.widgetJson.parameters.map(parameter => ({
            ...parameter,
            idDataAttribute: +parameter.dataAttribute,
            ...(parameter.yAxisProperties && {
                yAxisMin: parameter.yAxisProperties.min || null,
                yAxisMax: parameter.yAxisProperties.max || null,
                yAxisInterval: parameter.yAxisProperties.interval || null,
            }),
        }));
        const { parsedParameters } = parseParameters(parameters, this.devices);
        this.widget = {
            ...this.widget,
            title: this.lastMessageWithWidget.widgetJson.name,
            parameters: parsedParameters,
            yAxisProperties: {
                ...(this.lastMessageWithWidget.widgetJson.yAxisProperties && {
                    interval:
                        this.lastMessageWithWidget.widgetJson.yAxisProperties.globalInterval ||
                        null,
                    min: this.lastMessageWithWidget.widgetJson.yAxisProperties.globalMin || null,
                    max: this.lastMessageWithWidget.widgetJson.yAxisProperties.globalMax || null,
                }),
                method: this.localizedYAxisCustomisationOptions[
                    this.lastMessageWithWidget.widgetJson.yAxisScaling - 1
                ],
            },
        };
    }

    get localizedYAxisCustomisationOptions() {
        return Y_AXIS_CUSTOMISATION_OPTIONS.map(({ name, value }) => {
            return { value, name: this.$t('custom_widgets.y_axis_options.' + name) };
        });
    }

    toggleChatOptionsPopup(chatIdentifier, event) {
        if (this.activeChatOptionsPopup === chatIdentifier) {
            this.activeChatOptionsPopup = null;
        } else {
            this.activeChatOptionsPopup = chatIdentifier;

            this.$nextTick(() => {
                const popup = document.getElementById(
                    `vrm-ai-assistant__chat-options-popup--${chatIdentifier}`,
                );
                const button = event.target.closest('.vrm-ai-assistant__sidebar-more-button');

                if (popup && button) {
                    const buttonRect = button.getBoundingClientRect();
                    const sidebarWidth = 14 * 16;

                    popup.style.left = `${sidebarWidth + 8}px`;
                    popup.style.top = `${buttonRect.top + 24}px`;
                }
            });
        }
        event.stopPropagation();
    }

    closeChatOptionsPopup() {
        this.activeChatOptionsPopup = null;
    }

    renameChatAction(chat) {
        this.activeChatOptionsPopup = null;
        requestAnimationFrame(() => {
            this.renamingChatIdentifier = chat.chatIdentifier;
            this.renameInputValue = chat.name;

            this.$nextTick(() => {
                const inputRef = this.$refs[`renameInput-${chat.chatIdentifier}`];
                if (inputRef && inputRef[0]) {
                    inputRef[0].focus();
                    inputRef[0].select();
                }
            });
        });
    }

    async saveRename(chat) {
        if (!this.renameInputValue.trim()) {
            this.cancelRename();
            return;
        }

        const newName = this.renameInputValue.trim();
        const originalName = chat.name;

        if (newName === originalName) {
            this.cancelRename();
            return;
        }

        if (newName.length > 255) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('ai-assistant.chat_name_too_long', {
                    i18nFallback: 'Chat name cannot exceed 255 characters.',
                }),
            });
            return;
        }

        try {
            await this.ApiService.call2(
                `/installations/${this.idSite}/ai-chats/${chat.idAiChat}/update`,
                { name: newName },
                { method: 'PATCH', skipResultChecks: true, throwOnError: true },
            );

            chat.name = newName;
            this.renamingChatIdentifier = null;
            this.renameInputValue = '';

            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('ai-assistant.chat_renamed_successfully', {
                    i18nFallback: 'Chat renamed successfully!',
                }),
            });
        } catch (error) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('ai-assistant.error_renaming_chat', {
                    i18nFallback: 'There was an error renaming the chat, please try again.',
                }),
            });
            this.renameInputValue = originalName;
        }
    }

    cancelRename() {
        this.renamingChatIdentifier = null;
        this.renameInputValue = '';
    }

    async deleteChatAction(chat) {
        this.closeChatOptionsPopup();

        const deleteConfirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, {
            chatName: chat.name,
            message: this.$t('ai-assistant.confirm_deletion_message_chat', {
                i18nFallback: `Are you sure you want to delete the chat?`,
            }),
        });

        if (!deleteConfirmed) {
            return;
        }
        try {
            await this.ApiService.call2(
                `/installations/${this.idSite}/ai-chats/${chat.idAiChat}/delete`,
                null,
                { method: 'DELETE', skipResultChecks: true, throwOnError: true },
            );

            const chatIndex = this.chats.findIndex(c => c.chatIdentifier === chat.chatIdentifier);
            if (chatIndex !== -1) {
                this.chats.splice(chatIndex, 1);
            }

            if (this.currentChatIdentifier === chat.chatIdentifier) {
                if (this.chats.length > 0) {
                    this.openChat(this.chats[0]);
                } else {
                    await this.createNewChat();
                }
            }

            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('ai-assistant.chat_deleted_successfully', {
                    i18nFallback: 'Chat deleted successfully!',
                }),
            });
        } catch (error) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('ai-assistant.error_deleting_chat', {
                    i18nFallback: 'There was an error deleting the chat, please try again.',
                }),
            });
        }
    }

    mounted() {
        document.addEventListener('click', this.closeChatOptionsPopup);
    }

    beforeDestroy() {
        document.removeEventListener('click', this.closeChatOptionsPopup);
    }
}
</script>
